import {
  TextField,
  Box,
  Button,
  Grid,
  InputAdornment,
  Typography,
  CircularProgress,
  MenuItem,
  Popover,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from "@mui/material";
import * as XLSX from 'xlsx'
import React, { useContext, useEffect, useState, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import Layout from "components/structure/Layout";
import MUVSubGrowerTable from "./MUVSubGrowerTable";
import { SnackbarContext } from "context/snackBarContext";
import Service from "../../../services/ManageUnannouncedVisitorSubGrowerService";
import CustomPagination from "../Import/Common/CustomPagination";
import HierarchyDropdown from "../ManageUnannouncedVisitor/HierarchyDropdown";
import CommonService from "../../../services/CommonService";
import CheckboxTick from "assets/CheckboxTick.svg";
import CheckboxUntick from "assets/CheckboxUntick.svg";
import { equalsIgnoreCase, hasSelectedOption } from "utils/utils";

const MUVSubGrower = () => {
  const [loading, setLoading] = useState(false);
  const [visitorData, setVisitorData] = useState([]);
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
  const [cropYears, setCropYears] = useState([]);
  const [selectedCropYear, setSelectedCropYear] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [hierarchyData, setHierarchyData] = useState([]);
  const [selectedHierarchy, setSelectedHierarchy] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const operationalLevelRef = useRef(null);
  const [popoverWidth, setPopoverWidth] = useState(0);
  const [selectedCropStage, setSelectedCropStage] = useState("");
  const [cropStages, setCropStages] = useState([]);
  const [statusPickList, setStatusPickList] = useState([]);
  const [selectedStatusPickList, setSelectedStatusPickList] = useState([]);
  const [cropSeasons, setCropSeasons] = useState([]);
  const [selectedCropSeason, setSelectedCropSeason] = useState("");
  const [selectedSubGrowerStatus, setSelectedSubGrowerStatus] = useState([]);
  const [subGrowerStatusData, setSubGrowerStatusData] = useState([]);
  const [verificationStatusData, setVerificationStatusData] = useState([]);
  const [selectedVerificationStatus, setSelectedVerificationStatus] = useState("all");

  // Add these refs at the top with other state declarations
  const cropSeasonRef = useRef(null);
  const statusRef = useRef(null);
  const assignedFarmersRef = useRef(null);
  const subGrowerStatusRef = useRef(null);
  const verificationStatusRef = useRef(null);

  // Add these state variables for dropdown widths
  const [cropSeasonWidth, setCropSeasonWidth] = useState(0);
  const [statusWidth, setStatusWidth] = useState(0);
  const [assignedFarmersWidth, setAssignedFarmersWidth] = useState(0);
  const [subGrowerStatusWidth, setSubGrowerStatusWidth] = useState(0);
  const [verificationStatusWidth, setVerificationStatusWidth] = useState(0);

  // Add ref to track if first API call has been made
  const hasFirstCallBeenMade = useRef(false);

  useEffect(() => {
    fetchCropYears();
    fetchCropStages();
    fetchStatusPickList();
    initializeSubGrowerStatusData();
    initializeVerificationStatusData();
  }, []);

  useEffect(() => {
    if (selectedCropYear) {
      fetchHierarchyData(selectedCropYear);
    } else {
      setHierarchyData([]);
      setSelectedHierarchy([]);
      setSelectedLocations([]);
    }
  }, [selectedCropYear]);

  useEffect(() => {
    setCurrentPage(0);

    if (selectedCropYear && selectedCropStage && selectedLocations.length > 0) {
      // Use hasFirstCallBeenMade ref to determine if this should be initial
      const shouldBeInitial = !hasFirstCallBeenMade.current;
      fetchVisitorData(selectedCropYear, 0, itemsPerPage, shouldBeInitial);
    }
  }, [
    selectedCropStage,
    selectedStatusPickList,
    selectedLocations,
    assignedFilter,
    selectedSubGrowerStatus,
    selectedVerificationStatus,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(0);

      if (
        selectedCropYear &&
        selectedCropStage &&
        selectedLocations.length > 0
      ) {
        // Search changes should not be treated as initial
        fetchVisitorData(selectedCropYear, 0, itemsPerPage, false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchKeyword]);

  useEffect(() => {
    if (selectedCropYear) {
      // Reset all filters when crop year changes
      setCurrentPage(0);
      setSelectedCropSeason("");
      setSelectedCropStage("");
      setSearchKeyword("");
      setAssignedFilter("all");
      setSelectedVerificationStatus("all");
      setVisitorData([]);
      setTotalItems(0);
      
      // Reset the initial load flag so next API call is treated as initial
      hasFirstCallBeenMade.current = false;
      
      // Reset hierarchy to all selected
      if (hierarchyData.length > 0) {
        const { allIds, allLocations } = getAllNodesData(hierarchyData);
        setSelectedHierarchy(allIds);
        setSelectedLocations(allLocations);
      }
      
      // Reset status picklist to all selected
      if (statusPickList.length > 0) {
        setSelectedStatusPickList([...statusPickList]);
      }
      
      // Reset sub grower status to all selected
      if (subGrowerStatusData.length > 0) {
        setSelectedSubGrowerStatus([...subGrowerStatusData]);
      }
      
      // Fetch crop stages for the new crop year
      fetchCropStages();
    }
  }, [selectedCropYear, hierarchyData, statusPickList, subGrowerStatusData]);

  useEffect(() => {
    if (selectedCropSeason) {
      // Find the selected crop season
      const selectedSeasonData = cropSeasons.find(
        (season) => season.cropSeasonId === selectedCropSeason
      );

      // Filter only active crop stages and map to dropdown format
      const activeCropStages = selectedSeasonData?.cropStages
        ?.filter((stage) => stage.isActive)
        .map((stage) => ({
          value: stage.cropStageCalendarId,
          label: stage.cropStageName,
          isVisitCompliance: stage.isVisitCompliance,
          cropStageCalendarId: stage.cropStageCalendarId,
        })) || [];

      setCropStages(activeCropStages);

      // Set the first crop stage as default if there are any stages
      if (activeCropStages.length > 0) {
        const firstCropStage = activeCropStages[0];
        setSelectedCropStage(firstCropStage.value);
      } else {
        setSelectedCropStage("");
        setVisitorData([]);
        setTotalItems(0);
        setCurrentPage(0);
      }
    }
  }, [selectedCropSeason, cropSeasons]);

  const fetchCropYears = async () => {
    try {
      const response = await Service.getCropYears();
      if (response?.data) {
        // Set all crop years to state
        setCropYears(response.data.response || []);

        // Find the current crop year
        const currentYear = response.data.response.find(
          (year) => year.isCurrentCropYear
        );

        // Set the selected crop year
        if (currentYear) {
          setSelectedCropYear(currentYear.cropYear);
        } else if (response.data.response.length > 0) {
          setSelectedCropYear(response.data.response[0].cropYear);
        }
      }
    } catch (error) {
      console.error("Error fetching crop years:", error);
      setSeverity("error");
      setMessage(error?.response?.data?.message || error?.message);
      setOpenAlert(true);
    }
  };

  const fetchHierarchyData = async (cropYear = selectedCropYear) => {
    if (!cropYear) return;

    try {
      const response = await Service.getHierarchyDropdown({ isMuv: true, cropYear });
      if (response?.data?.response) {
        const hierarchyData = response.data.response;
        setHierarchyData(hierarchyData);

        // Get all location codes and IDs
        const { allIds, allLocations } = getAllNodesData(hierarchyData);
        setSelectedHierarchy(allIds);
        setSelectedLocations(allLocations);
      }
    } catch (error) {
      console.error("Error fetching hierarchy data:", error);
      setSeverity("error");
      setMessage(error?.response?.data?.message || error?.message);
      setOpenAlert(true);
    }
  };

  // Modify getAllNodesData function to get all nodes and their children
  const getAllNodesData = (data) => {
    const allIds = [];
    const allLocations = new Set();

    const traverse = (items) => {
      items.forEach((item) => {
        allIds.push(item.ihId);
        allLocations.add(item.ihLocationCode);

        if (item.childHierarchy) {
          traverse(item.childHierarchy);
        }
      });
    };

    traverse(data);
    return {
      allIds,
      allLocations: Array.from(allLocations),
    };
  };

  // Modify getFilteredLocationCodes function to get only selected nodes' location codes
  const getFilteredLocationCodes = (data, selectedIds) => {
    const locationCodes = new Set();

    const traverse = (items) => {
      items.forEach((item) => {
        // Only add location code if the current item's ID is directly selected
        if (selectedIds.includes(item.ihId)) {
          locationCodes.add(item.ihLocationCode);
        }

        // Continue traversing child hierarchies
        if (item.childHierarchy) {
          traverse(item.childHierarchy);
        }
      });
    };

    traverse(data);
    return Array.from(locationCodes);
  };

  const fetchCropStages = async () => {
    try {
      if (!selectedCropYear) return;

      const response = await Service.getCropStage({
        cropYear: selectedCropYear,
      });

      if (response?.data?.response) {
        // Set crop seasons
        setCropSeasons(response.data.response);

        // Always select the first crop season when crop year changes
        if (response.data.response.length > 0) {
          const firstCropSeason = response.data.response[0];
          setSelectedCropSeason(firstCropSeason.cropSeasonId);
        } else {
          // If no crop seasons, reset all related states
          setSelectedCropSeason("");
          setSelectedCropStage("");
          setCropStages([]);
          setVisitorData([]);
          setTotalItems(0);
          setCurrentPage(0);
        }
      }
    } catch (error) {
      console.error("Error fetching crop stages:", error);
      setSeverity("error");
      setMessage(error?.response?.data?.message || error?.message);
      setOpenAlert(true);
      
      // Reset all states in case of error
      setCropSeasons([]);
      setSelectedCropSeason("");
      setSelectedCropStage("");
      setCropStages([]);
      setVisitorData([]);
      setTotalItems(0);
      setCurrentPage(0);
    }
  };

  const fetchStatusPickList = async () => {
    try {
      const response = await CommonService.fetchPickList({
        pickListCodes: ["CSSSTATUV"],
      });
      let data = response?.data?.response?.CSSSTATUV;
      setStatusPickList(data);
      // Set all statuses selected by default
      setSelectedStatusPickList(data);
    } catch (error) {
      console.error("Error fetching status picklist:", error);
      setStatusPickList([]);
      setSelectedStatusPickList([]);
    }
  };

  const handleStatusChange = (event) => {
    const selectedValue = event.target.value;

    // Handle "Select All" option
    if (hasSelectedOption(selectedValue, "all")) {
      setSelectedStatusPickList(
        selectedStatusPickList.length === statusPickList.length
          ? []
          : [...statusPickList]
      );
    } else {
      setSelectedStatusPickList(selectedValue);
    }
  };

  const getSurveyStatusesPayload = () =>
    selectedStatusPickList.map((status) => {
      const value = status.pickListDescription || status.pickListItemCode;
      return value === "In Progress" ? "InProgress" : value;
    });

  const buildVisitorListParams = (cropYear) => {
    const selectedCropStageData = cropStages.find(
      (stage) => stage.value === selectedCropStage
    );

    const params = {
      cropYear,
      ...(searchKeyword.trim() && { keyword: searchKeyword.trim() }),
      ...(assignedFilter !== "all" && {
        isAssigned: assignedFilter === "true",
      }),
      ...(selectedLocations.length > 0 &&
        selectedHierarchy.length > 0 && {
          locationCodeCommaSeparated: selectedLocations.join(","),
        }),
      cropStageCalendarId: selectedCropStageData?.cropStageCalendarId,
      surveyStatuses: getSurveyStatusesPayload(),
      ...(selectedSubGrowerStatus.length === 1 && {
        subGrowerStatus: selectedSubGrowerStatus[0].value,
      }),
      ...(selectedVerificationStatus !== "all" && {
        verificationStatus: selectedVerificationStatus,
      }),
    };

    Object.keys(params).forEach((key) => {
      if (
        params[key] === undefined ||
        params[key] === null ||
        params[key] === ""
      ) {
        delete params[key];
      }
    });

    return params;
  };

  const fetchVisitorData = async (cropYear, pageNumber = 0, pageSize = 20, isInitial = false) => {
    try {
      setLoading(true);

      // Mark that the first call has been made
      if (isInitial) {
        hasFirstCallBeenMade.current = true;
      }

      const params = buildVisitorListParams(cropYear);

      const response = await Service.getVisitorList(params ,pageNumber ,pageSize);
      if (response?.data) {
        setVisitorData(response.data.response.subGrowerManageUnannouncedVisit || []);
        setTotalItems(response.data.response.totalItems || 0);
        setCurrentPage(pageNumber);

      }
    } catch (error) {
      setVisitorData([]);
      setTotalItems(0);
      console.error("Error fetching visitor data:", error);
      setSeverity("error");
      setMessage(error?.response?.data?.message || error?.message);
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const paginate = (pageNumber, pageSize) => {
    // Pagination should not be treated as initial load
    fetchVisitorData(selectedCropYear, pageNumber - 1, pageSize, false);
  };

  const handleOperationalLevelClick = (event) => {
    if (operationalLevelRef.current) {
      const width = operationalLevelRef.current.offsetWidth;
      setPopoverWidth(width);
    }
    setAnchorEl(event.currentTarget);
  };

  const handleOperationalLevelClose = () => {
    setAnchorEl(null);
  };

  // Update the handleHierarchySelect function
  const handleHierarchySelect = (selectionData) => {
    const { selectedIds } = selectionData;
    setSelectedHierarchy(selectedIds);

    // Get filtered location codes
    const filteredLocations = getFilteredLocationCodes(
      hierarchyData,
      selectedIds
    );
    setSelectedLocations(filteredLocations);
  };

  // Update useEffect to handle resize observations
  useEffect(() => {
    if (operationalLevelRef.current) {
      // Create new ResizeObserver
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setPopoverWidth(entry.target.offsetWidth);
        }
      });

      resizeObserver.observe(operationalLevelRef.current);

      // Cleanup
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  // Add this useEffect for handling resize observers
  useEffect(() => {
    const refs = [
      { ref: cropSeasonRef, setWidth: setCropSeasonWidth },
      { ref: statusRef, setWidth: setStatusWidth },
      { ref: assignedFarmersRef, setWidth: setAssignedFarmersWidth },
      { ref: subGrowerStatusRef, setWidth: setSubGrowerStatusWidth },
      { ref: verificationStatusRef, setWidth: setVerificationStatusWidth },
    ];

    const observers = refs.map(({ ref, setWidth }) => {
      if (ref.current) {
        const observer = new ResizeObserver((entries) => {
          for (const entry of entries) {
            setWidth(entry.target.offsetWidth);
          }
        });
        observer.observe(ref.current);
        return observer;
      }
      return null;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  const initializeSubGrowerStatusData = () => {
    const data = [
      { pickListItemCode: "active", pickListDescription: "Active", value: true },
      { pickListItemCode: "inactive", pickListDescription: "Inactive", value: false },
    ];
    setSubGrowerStatusData(data);
    setSelectedSubGrowerStatus(data);
  };

  const handleSubGrowerStatusChange = (event) => {
    const selectedValue = event.target.value;

    // Handle "Select All" option
    if (hasSelectedOption(selectedValue, "all")) {
      setSelectedSubGrowerStatus(
        selectedSubGrowerStatus.length === subGrowerStatusData.length
          ? []
          : [...subGrowerStatusData]
      );
    } else {
      setSelectedSubGrowerStatus(selectedValue);
    }
  };

  const initializeVerificationStatusData = () => {
    setVerificationStatusData([
      { value: "all", label: "All" },
      { value: "Pending", label: "Pending" },
      { value: "Verified", label: "Verified" },
    ]);
  };

   const handleExportExcel= async()=>{
 
    const selectedCropStageData = cropStages.find(
      (stage) => stage.value === selectedCropStage
    );
    const cropStageName = selectedCropStageData?.label || "";

    const params = buildVisitorListParams(selectedCropYear);

    try{
    setLoading(true);
    // Call the API for export
    const response = await Service.getVisitorList(params,0 ,999999);
    setLoading(false);


    const muvData = response?.data?.response?.subGrowerManageUnannouncedVisit || [];

    if (muvData.length === 0) {
      setSeverity("info");
      setMessage("No data found to export.");
      setOpenAlert(true);
      return;
    }
    const exportData = muvData.map(item => ({
          'Crop Year': selectedCropYear,
          'Property Number': item.propertyFarmNumber,
          'Grower Number': item.allGrowerNumber,
          'Farmer Name': item.allFarmerName,
          'Crop Stage': cropStageName,
          'Sub Grower Code': item.permanentCode || item.temporaryCode,
          'Sub Grower Name': item.subGrowerName,
          'Survey Status': item.surveyStatus,
          'Status': item.subGrowerStatus ? 'Active' : 'Inactive',
          'U.V. Name': item.userName,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, "MUV - Sub Grower");
        const filename = `Manage Unannounced Visit - Sub Grower.csv`;
        // Write and download the file as CSV
        XLSX.writeFile(wb, filename, { bookType: 'csv' });
    }
    catch (error) {
    setLoading(false);
    setSeverity("error");
    setMessage(error?.response?.data?.message || error?.message);
    setOpenAlert(true);
  }

  }

  return (
    <Layout
      title="Manage Unannounced Visit - Sub Grower"
      style={{ backgroundColor: "red" }}
    >
      <Box
        className="muv-container"
        sx={{ position: "relative", minHeight: "100vh" }}
      >
        <Typography
          sx={{
            color: "#102A62", // Primary Blue color
            fontFamily: "Montserrat",
            fontWeight: 600,
            fontSize: "20px",
            lineHeight: "30px",
            letterSpacing: 0,
            marginBottom: 2, // Optional: adds some spacing below the title
          }}
        >
          Manage Unannounced Visit - Sub Grower
        </Typography>

        <Grid
          container
          spacing={1}
          justifyContent="center"
          alignItems="center"
          sx={{
            width: "100%",
            marginTop: 1,
            marginLeft: 0.1,
            paddingTop: 3,
            paddingRight: 1,
            paddingBottom: 3,
            backgroundColor: "#EFEFEF",
          }}
        >
          <Grid item xs={12} sm={4} sx={{ order: { xs: 3, sm: 3 } }}>
            <TextField
              ref={operationalLevelRef}
              fullWidth
              label="Operational Level"
              variant="outlined"
              onClick={handleOperationalLevelClick}
              value={`${selectedHierarchy.length} selected`}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#00000099",
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",
                  borderRadius: "5px",
                  "& input": {
                    height: "46px",
                    padding: "0 14px",
                    boxSizing: "border-box",
                  },
                  "& fieldset": {
                    borderColor: "#C4C4C4",
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C4C4C4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4C4C4",
                  },
                },
              }}
            />
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleOperationalLevelClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  height: "auto",
                  width: popoverWidth || "auto", // Use stored width directly
                  maxHeight: 400,
                  overflow: "auto",
                },
              }}
            >
              <div style={{ overflow: "auto" }}>
                <HierarchyDropdown
                  hierarchyData={hierarchyData}
                  selectedItems={selectedHierarchy}
                  onSelectionChange={handleHierarchySelect}
                  useWrappedText={false}                />
              </div>
            </Popover>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ order: { xs: 1, sm: 1 } }}>
            <TextField
              fullWidth
              select
              label="Crop Year"
              variant="outlined"
              value={selectedCropYear}
              onChange={(e) => setSelectedCropYear(e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#00000099",
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",
                  borderRadius: "5px",
                  "& fieldset": {
                    borderColor: "#C4C4C4",
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C4C4C4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4C4C4",
                  },
                },
              }}
            >
              {cropYears.map((yearData) => (
                <MenuItem key={yearData.cropYearUid} value={yearData.cropYear}>
                  {yearData.cropYearName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ order: { xs: 2, sm: 2 } }}>
            <TextField
              ref={cropSeasonRef}
              fullWidth
              select
              label="Crop Season"
              variant="outlined"
              value={selectedCropSeason}
              onChange={(e) => setSelectedCropSeason(e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#00000099",
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      width: cropSeasonWidth || 'auto',
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minHeight: 'unset',
                        padding: '6px 16px',
                      }
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",
                  borderRadius: "5px",
                  "& fieldset": {
                    borderColor: "#C4C4C4",
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C4C4C4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4C4C4",
                  },
                },
              }}
            >
              {cropSeasons.map((seasonData) => (
                <MenuItem 
                  key={seasonData.cropSeasonId} 
                  value={seasonData.cropSeasonId}
                >
                  {seasonData.cropSeasonName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ order: { xs: 5, sm: 5 } }}>
            <TextField
              ref={statusRef}
              fullWidth
              select
              label="F.T Survey Status"
              variant="outlined"
              value={selectedStatusPickList || []}
              onChange={handleStatusChange}
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  selected
                    .map((status) => status.pickListDescription)
                    .join(", "),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      width: statusWidth || 'auto',
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minHeight: 'unset',
                        padding: '6px 16px',
                      }
                    },
                  },
                },
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#00000099",
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",
                  borderRadius: "5px",
                  "& fieldset": {
                    borderColor: "#C4C4C4",
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C4C4C4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4C4C4",
                  },
                },
              }}
            >
              <MenuItem value="all">
                <ListItemIcon>
                  <Checkbox
                    checked={
                      selectedStatusPickList?.length === statusPickList?.length
                    }
                    indeterminate={
                      selectedStatusPickList?.length > 0 &&
                      selectedStatusPickList?.length < statusPickList?.length
                    }
                    icon={<img src={CheckboxUntick} alt="Unchecked" />}
                    checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                  />
                </ListItemIcon>
                <ListItemText primary="Select All" />
              </MenuItem>
              {statusPickList?.map((data) => (
                <MenuItem key={data?.pickListItemCode} value={data}>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedStatusPickList.some(
                        (item) =>
                          equalsIgnoreCase(item.pickListItemCode, data.pickListItemCode)
                      )}
                      icon={<img src={CheckboxUntick} alt="Unchecked" />}
                      checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                    />
                  </ListItemIcon>
                  <ListItemText primary={data.pickListDescription} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ order: { xs: 6, sm: 6 } }}>
            <TextField
              ref={subGrowerStatusRef}
              select
              fullWidth
              label="Status"
              variant="outlined"
              value={selectedSubGrowerStatus}
              onChange={handleSubGrowerStatusChange}
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  selected
                    .map((status) => status.pickListDescription)
                    .join(", "),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      width: subGrowerStatusWidth || 'auto',
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minHeight: 'unset',
                        padding: '6px 16px',
                      }
                    },
                  },
                },
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#00000099",
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",
                  borderRadius: "5px",
                  "& fieldset": {
                    borderColor: "#C4C4C4",
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C4C4C4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4C4C4",
                  },
                },
              }}
            >
              <MenuItem value="all">
                <ListItemIcon>
                  <Checkbox
                    checked={
                      selectedSubGrowerStatus?.length === subGrowerStatusData?.length
                    }
                    indeterminate={
                      selectedSubGrowerStatus?.length > 0 &&
                      selectedSubGrowerStatus?.length < subGrowerStatusData?.length
                    }
                    icon={<img src={CheckboxUntick} alt="Unchecked" />}
                    checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                  />
                </ListItemIcon>
                <ListItemText primary="Select All" />
              </MenuItem>
              {subGrowerStatusData?.map((status) => (
                <MenuItem key={status.pickListItemCode} value={status}>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedSubGrowerStatus.some(
                        (item) => equalsIgnoreCase(item.pickListItemCode, status.pickListItemCode)
                      )}
                      icon={<img src={CheckboxUntick} alt="Unchecked" />}
                      checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                    />
                  </ListItemIcon>
                  <ListItemText primary={status.pickListDescription} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={1}
          justifyContent="flex-start"
          alignItems="center"
          sx={{
            width: "100%",
            marginLeft: 0.1,
            paddingBottom: 3,
            backgroundColor: "#EFEFEF",
          }}
        >
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#00000099" }} />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#00000099",
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",
                  borderRadius: "5px",
                  "& input": {
                    height: "46px",
                    padding: "0",
                    boxSizing: "border-box",
                  },
                  "& fieldset": {
                    borderColor: "#C4C4C4",
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C4C4C4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4C4C4",
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1.7}>
            <TextField
              ref={verificationStatusRef}
              fullWidth
              select
              label="Verification Status"
              variant="outlined"
              value={selectedVerificationStatus}
              onChange={(e) => setSelectedVerificationStatus(e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#00000099",
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      width: verificationStatusWidth || 'auto',
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minHeight: 'unset',
                        padding: '6px 16px',
                      }
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",
                  borderRadius: "5px",
                  "& fieldset": {
                    borderColor: "#C4C4C4",
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C4C4C4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4C4C4",
                  },
                },
              }}
            >
              {verificationStatusData.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={1.7}>
            <TextField
              ref={assignedFarmersRef}
              fullWidth
              select
              label="Assigned Sub Grower"
              variant="outlined"
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#00000099",
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      width: assignedFarmersWidth || 'auto',
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minHeight: 'unset',
                        padding: '6px 16px',
                      }
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",
                  borderRadius: "5px",
                  "& fieldset": {
                    borderColor: "#C4C4C4",
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C4C4C4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4C4C4",
                  },
                },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Assigned Sub Grower</MenuItem>
              <MenuItem value="false">Unassigned Sub Grower</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3.5}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() =>
                    // Manual refresh should not be treated as initial load
                    fetchVisitorData(selectedCropYear, currentPage, itemsPerPage, false)
                  }
                  startIcon={<RefreshIcon />}
                  sx={{
                    backgroundColor: "white",
                    color: "black",
                    "&:hover": {
                      backgroundColor: "white",
                    },
                    boxShadow: "none",
                    height: "46px",
                    border: "1px solid #C4C4C4",
                    fontFamily: "Montserrat",
                  }}
                >
                  Refresh Page
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: "#0F2B63",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#0F2B63",
                    },
                    boxShadow: "none",
                    height: "46px",
                    fontFamily: "Montserrat",
                    '&.Mui-disabled': {
                        backgroundColor: "#102A62", 
                        color: "#ffffff",    
                        opacity: 0.6,          
                        },
                  }}
                  // TODO: Add export logic here
                  onClick={handleExportExcel}
                  disabled={loading || visitorData.length===0}
                >
                  Export to Excel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <>
            <MUVSubGrowerTable data={visitorData} cropYear={selectedCropYear} />
            {visitorData.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  bottom: "-70px", // Changed from 70px to 0
                }}
              >
                <CustomPagination
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={(v) => setItemsPerPage(v)}
                  totalItems={totalItems}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </div>
            )}
          </>
        )}
      </Box>
    </Layout>
  );
};

export default MUVSubGrower;
